/**
 * Drive REST adapter. Every path matches apps/api/src/routes/files.ts.
 *
 * NOTE ON URLS: `downloadUrl`, `thumbnailUrl` and `previewUrl` come back from
 * the API as absolute PATHS (`/v1/files/…`), not signed URLs, and they are
 * meant to be put straight into an `<img src>` or an anchor. They authorise
 * through the httpOnly content ticket (`useContentSession`), which is the only
 * credential a browser-constructed request can carry.
 */

import type { CyphexFile } from '@cyphex/types/files';
import { apiDelete, apiGet, apiPatch, apiPost } from '@/lib/api/client';

export type FileSort = 'recent' | 'oldest' | 'name' | 'largest';

export interface FileBrowseQuery {
  readonly q?: string;
  readonly sort: FileSort;
  readonly fileClass?: string;
}

export interface FilePage {
  readonly files: CyphexFile[];
  readonly nextCursor: string | null;
}

export interface ThumbnailBody {
  readonly mimeType: 'image/webp' | 'image/jpeg';
  readonly dataBase64: string;
  readonly width: number;
  readonly height: number;
}

const queryString = (query: FileBrowseQuery, cursor: string | undefined): string => {
  const params = new URLSearchParams({ sort: query.sort, limit: '40' });
  if (query.q !== undefined && query.q !== '') params.set('q', query.q);
  if (query.fileClass !== undefined && query.fileClass !== '') {
    params.set('fileClass', query.fileClass);
  }
  if (cursor !== undefined) params.set('cursor', cursor);
  return `?${params.toString()}`;
};

export const filesApi = {
  browse: (query: FileBrowseQuery, cursor?: string): Promise<FilePage> =>
    apiGet<FilePage>(`/files${queryString(query, cursor)}`),

  rename: (fileId: string, name: string): Promise<{ ok: boolean }> =>
    apiPatch<{ ok: boolean }>(`/files/${fileId}`, { name }),

  setPinned: (fileId: string, isPinned: boolean): Promise<{ ok: boolean }> =>
    apiPatch<{ ok: boolean }>(`/files/${fileId}`, { isPinned }),

  remove: (fileId: string): Promise<{ ok: boolean }> =>
    apiDelete<{ ok: boolean }>(`/files/${fileId}`),

  /** Mints the httpOnly ticket that lets the BROWSER fetch file bytes. */
  openContentSession: (): Promise<{ expiresAt: string }> =>
    apiPost<{ expiresAt: string }>('/files/content-session'),

  putThumbnail: (fileId: string, body: ThumbnailBody): Promise<{ thumbnailUrl: string }> =>
    apiPost<{ thumbnailUrl: string }>(`/files/${fileId}/thumbnail`, body),
};
